import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useTabs from "../../../utils/useTab";
import { styles } from "../../styles";
import useAnalysis from "../../../utils/useAnalysis";
import dayjs from "dayjs";
import { configButtonAnalysis } from "../../../utils/constants";
import { FormProvider, useFormContext } from "react-hook-form";
import {
  AnalysisPhasesTritio,
  Calculation,
  Counting,
  PreparationTypes,
} from "@app/types/samples.types";
import { Remove } from "@app/components/Actions";
import {
  MAutocomplete,
  MButton,
  ResponsiveFormContainer,
} from "@app/components";
import useAuth from "@app/context/AuthContext";
import usePermission from "@app/context/PermissionContext";

const NavHistoryTritio: React.FC = () => {
  const theme = useTheme();
  const { tabValueAnalysis, setTabValueAnalysis } = useTabs();
  const [dialogData, setDialogData] = useState<any>({});
  const { isAdmin, isSuperAdmin } = usePermission();

  const {
    preparationsList,
    setPreparationsList,
    setCurrentPreparation,
    setCurrentCounting,
    setCurrentCalculation,
    currentPreparation,
    currentCounting,
    currentCalculation,
    remove,
  } = useAnalysis();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { user } = useAuth();

  const form = useFormContext();
  const selectedAnalysis = form.getValues("typeAnalysis");

  const filteredPreparationsList = preparationsList.filter((preparation) =>
    preparation?.analysis_type?.some(
      (analysis) => analysis.id === selectedAnalysis.id
    )
  );

  const fontSizeText = isSmallScreen
    ? "0.40rem"
    : isMediumScreen
    ? "0.50rem"
    : "0.60rem";

  const selectedStyle = {
    backgroundColor: theme.palette.action.selected,
    borderRadius: 4,
    padding: "0.5rem",
  };

  const selectedStyleActive = {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
    padding: "0.5rem",
  };

  const handleCloseModal = () => {
    clearDataForm();
    setDialogData({ active: false });
  };

  const handleOpenModal = (title: string) => {
    setDialogData({
      active: true,
      title: title,
    });
  };

  const onClickNewAction = () => {
    if (tabValueAnalysis === AnalysisPhasesTritio.PREPARATION) {
      const newPreparation = {
        id: undefined,
        analyst: user,
        start_date: dayjs().toISOString(),
        countings: [],
        analysis_type: [selectedAnalysis],
      };
      setPreparationsList([...preparationsList, newPreparation]);
      setCurrentPreparation(undefined, filteredPreparationsList.length, true);
    } else if (tabValueAnalysis === AnalysisPhasesTritio.COUNTING) {
      handleOpenModal("Nuevo Contaje");
    } else if (tabValueAnalysis === AnalysisPhasesTritio.CALCULATION) {
      handleOpenModal("Nuevo Cálculo");
    }
  };

  const preparationSelected = form.watch("preparationSelected");
  const typeAnalysis = form.watch("typeAnalysis");

  useEffect(() => {
    form.setValue("countingSelected", null);
  }, [preparationSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePreparationClick = (
    prepIndex: number,
    event: React.MouseEvent | null
  ) => {
    if (event) event.stopPropagation();
    const selectedPreparation = filteredPreparationsList[prepIndex];
    setCurrentPreparation(
      selectedPreparation.id,
      prepIndex,
      selectedPreparation?.countings?.length === 0
    );
    setCurrentCounting(undefined, undefined, undefined);
    setCurrentCalculation(undefined, undefined, undefined);
    setTabValueAnalysis(0);
  };

  const handleCountingClick = (
    prepIndex: number,
    countIndex: number,
    event: React.MouseEvent | null
  ) => {
    if (event) event.stopPropagation();
    const selectedCounting =
      filteredPreparationsList[prepIndex].countings?.[countIndex];
    setCurrentPreparation(filteredPreparationsList[prepIndex].id, prepIndex);
    setCurrentCounting(
      selectedCounting?.id,
      countIndex,
      selectedCounting?.calculations &&
        selectedCounting?.calculations?.length === 0
    );
    setCurrentCalculation(undefined, undefined, undefined);
    setTabValueAnalysis(1);
  };

  const handleCalculationClick = (
    prepIndex: number,
    countIndex: number,
    calcIndex: number,
    event: React.MouseEvent | null
  ) => {
    if (event) event.stopPropagation();
    const selectedCalculation =
      filteredPreparationsList[prepIndex].countings?.[countIndex].calculations[
        calcIndex
      ];
    setCurrentPreparation(filteredPreparationsList[prepIndex].id, prepIndex);
    setCurrentCounting(
      filteredPreparationsList[prepIndex].countings?.[countIndex].id,
      countIndex
    );
    setCurrentCalculation(
      selectedCalculation?.id,
      calcIndex,
      selectedCalculation?.result
    );
    setTabValueAnalysis(2);
  };

  const clearDataForm = () => {
    form.setValue("preparationSelected", null);
    form.setValue("countingSelected", null);
  };

  const handleModalSave = () => {
    const data = form.getValues();
    const { preparationSelected, countingSelected } = data;

    if (tabValueAnalysis === AnalysisPhasesTritio.COUNTING) {
      if (!preparationSelected || !preparationSelected.id) {
        form.setError("preparationSelected", {
          type: "manual",
          message: "La preparación es requerida",
        });
        return;
      }
      const updatedPreparations = preparationsList.map((preparation) =>
        preparation.id === preparationSelected.id
          ? {
              ...preparation,
              countings: [
                ...(preparation.countings as Counting[]),
                {
                  id: undefined,
                  counting_date: dayjs().toISOString(),
                  calculations: [],
                },
              ],
            }
          : preparation
      );

      setPreparationsList(updatedPreparations);
      const updatedPreparaListIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        .findIndex((p) => p.id === preparationSelected.id);

      const selectedPreparation =
        updatedPreparations?.[
          preparationsList.findIndex((p) => p.id === preparationSelected.id)
        ];

      const countingIndex = selectedPreparation?.countings
        ? selectedPreparation.countings.length - 1
        : 0;

      setCurrentPreparation(preparationSelected.id, updatedPreparaListIndex);
      setCurrentCounting(undefined, countingIndex, true);

      setTabValueAnalysis(1); // Cambiar a la pestaña de Contaje
    } else if (tabValueAnalysis === AnalysisPhasesTritio.CALCULATION) {
      if (!preparationSelected || !countingSelected) {
        form.setError("countingSelected", {
          type: "manual",
          message: "La preparación es requerida",
        });
        return;
      }
      const updatedPreparations = preparationsList.map((preparation) =>
        preparation.id === preparationSelected.id
          ? {
              ...preparation,
              countings: preparation.countings?.map((counting, index) =>
                counting.id === countingSelected.id
                  ? {
                      ...counting,
                      calculations: [
                        ...counting.calculations,
                        {
                          id: undefined,
                          start_date_time: dayjs().toISOString(),
                          end_date_time: dayjs().toISOString(),
                          counting_date: dayjs().toISOString(),
                        },
                      ],
                    }
                  : counting
              ),
            }
          : preparation
      );
      setPreparationsList(updatedPreparations);
      const updatedPreparationIndex = preparationsList.findIndex(
        (p) => p.id === preparationSelected.id
      );

      const updatedPreparaListIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        .findIndex((p) => p.id === preparationSelected.id);

      const updatedCountingIndex = preparationsList[
        updatedPreparationIndex
      ].countings?.findIndex((c) => c.id === countingSelected.id);

      const updatedCountingListIndex = preparationsList
        .filter((preparation) =>
          preparation.analysis_type.some(
            (analysis) => analysis.id === selectedAnalysis.id
          )
        )
        [updatedPreparaListIndex].countings?.findIndex(
          (c) => c.id === countingSelected.id
        );

      setCurrentPreparation(preparationSelected.id, updatedPreparaListIndex);
      setCurrentCounting(countingSelected.id, updatedCountingListIndex);

      const validUpdatedCountingIndex = updatedCountingIndex ?? 0;
      const selectedPreparation =
        updatedPreparations?.[updatedPreparationIndex];
      const selectedCounting =
        selectedPreparation?.countings?.[validUpdatedCountingIndex];
      const calculationsLength = selectedCounting?.calculations?.length;

      const currentCalculationUpload = calculationsLength
        ? calculationsLength - 1
        : 0;

      setCurrentCalculation(undefined, currentCalculationUpload);
      setTabValueAnalysis(2); // Cambiar a la pestaña de Cálculo
    }

    handleCloseModal();
  };

  const autoSelectNav = () => {
    let pIndex;
    let coIndex;
    let caIndex;
    filteredPreparationsList.map(
      (preparation: PreparationTypes, prepIndex: number) => {
        if (
          caIndex === undefined &&
          (!preparation.countings ||
            (preparation.countings && preparation.countings.length === 0))
        ) {
          pIndex = prepIndex;
        }
        preparation?.countings?.map(
          (counting: Counting, countIndex: number) => {
            if (
              caIndex === undefined &&
              (!counting.calculations ||
                (counting.calculations && counting.calculations.length === 0))
            ) {
              pIndex = prepIndex;
              coIndex = countIndex;
            }
            counting?.calculations?.map(
              (calculation: Calculation, calcIndex: number) => {
                if (calculation.result && calculation.result.to_reports) {
                  pIndex = prepIndex;
                  coIndex = countIndex;
                  caIndex = calcIndex;
                }
              }
            );
          }
        );
      }
    );

    if (caIndex !== undefined) {
      handleCalculationClick(pIndex, coIndex, caIndex, null);
      return;
    }

    if (coIndex !== undefined) {
      handleCountingClick(pIndex, coIndex, null);
      return;
    }

    if (pIndex !== undefined) {
      handlePreparationClick(pIndex, null);
      return;
    }
  };

  useEffect(() => {
    autoSelectNav();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Box sx={{ ...styles.navHistoryWrapper }}>
        {filteredPreparationsList.map(
          (preparation: PreparationTypes, prepIndex: number) => (
            <Box
              key={`preparation-${prepIndex}`}
              sx={{
                marginBottom: 2,
                ...(currentPreparation.index === prepIndex
                  ? tabValueAnalysis === AnalysisPhasesTritio.PREPARATION
                    ? selectedStyleActive
                    : selectedStyle
                  : {}),
              }}
              onClick={(event) => handlePreparationClick(prepIndex, event)}
            >
              <Box
                sx={{
                  fontSize: fontSizeText,
                  cursor: "pointer",
                }}
              >
                Preparación {prepIndex + 1}
                {preparation.countings?.length === 0 &&
                  (isAdmin || isSuperAdmin) && (
                    <Remove
                      onRemove={() =>
                        remove(
                          typeAnalysis.id,
                          "preparation",
                          currentPreparation?.id,
                          currentPreparation?.index
                        )
                      }
                    />
                  )}
              </Box>
              <Box sx={{ paddingLeft: 2 }}>
                {preparation?.countings?.map(
                  (counting: Counting, countIndex: number) => (
                    <Box
                      key={`counting-${countIndex}`}
                      sx={{
                        marginBottom: 1,
                        marginTop: counting.calculations?.length !== 0 ? 1 : 0,
                        ...(currentCounting?.index === countIndex &&
                        currentPreparation.index === prepIndex
                          ? tabValueAnalysis === AnalysisPhasesTritio.COUNTING
                            ? selectedStyleActive
                            : selectedStyle
                          : {}),
                      }}
                      onClick={(event) =>
                        handleCountingClick(prepIndex, countIndex, event)
                      }
                    >
                      <Box sx={{ fontSize: fontSizeText, cursor: "pointer" }}>
                        Contaje {countIndex + 1}
                        {counting.calculations?.length === 0 &&
                          (isAdmin || isSuperAdmin) && (
                            <Remove
                              onRemove={() =>
                                remove(
                                  typeAnalysis.id,
                                  "counting",
                                  currentCounting?.id,
                                  currentPreparation?.index,
                                  currentCounting?.index
                                )
                              }
                            />
                          )}
                      </Box>
                      <Box sx={{ paddingLeft: 2 }}>
                        {counting?.calculations?.map(
                          (calculation: Calculation, calcIndex: number) => (
                            <Box
                              key={`calculation-${calculation}-${calcIndex}-${countIndex}-${prepIndex}`}
                              sx={{
                                marginBottom: 1,
                                ...(currentCalculation?.index === calcIndex &&
                                currentCounting?.index === countIndex &&
                                currentPreparation.index === prepIndex
                                  ? tabValueAnalysis ===
                                    AnalysisPhasesTritio.CALCULATION
                                    ? selectedStyleActive
                                    : selectedStyle
                                  : {}),
                              }}
                              onClick={(event) =>
                                handleCalculationClick(
                                  prepIndex,
                                  countIndex,
                                  calcIndex,
                                  event
                                )
                              }
                            >
                              <Box
                                sx={{
                                  fontSize: fontSizeText,
                                  fontWeight: calculation?.result?.to_reports
                                    ? "bold"
                                    : "normal",
                                  cursor: "pointer",
                                }}
                              >
                                Cálculo {calcIndex + 1}
                                {isAdmin || isSuperAdmin ? (
                                  <Remove
                                    onRemove={() =>
                                      remove(
                                        typeAnalysis.id,
                                        "calculation",
                                        currentCalculation?.id,
                                        currentPreparation?.index,
                                        currentCounting?.index,
                                        currentCalculation?.index
                                      )
                                    }
                                  />
                                ) : null}
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                  )
                )}
              </Box>
            </Box>
          )
        )}
        <Box sx={{ ...styles.newPrepButton }}>
          {isAdmin || isSuperAdmin ? (
            <MButton
              variant="contained"
              sx={{ height: 1 }}
              onClick={onClickNewAction}
              text={configButtonAnalysis[tabValueAnalysis]?.titleButton || ""}
            />
          ) : null}
        </Box>
      </Box>

      <Modal
        open={!!dialogData.active}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={{ ...styles.modalContainer }}>
          <FormProvider {...form}>
            <Grid item xs={12} my={2}>
              <Typography variant="tagText">{dialogData.title}</Typography>
              <Divider sx={{ mt: 1 }} />
            </Grid>

            <ResponsiveFormContainer columns={2}>
              <MAutocomplete
                name="preparationSelected"
                options={filteredPreparationsList
                  .filter((prep) => prep.id)
                  .map((prep, index) => ({
                    ...prep,
                    index,
                  }))}
                label="Seleccione una preparación"
                getOptionLabel={(option) => `Preparación ${option.index + 1}`}
                validation={{
                  required: "La preparación es requerida",
                }}
              />
              {preparationSelected &&
                tabValueAnalysis === AnalysisPhasesTritio.CALCULATION && (
                  <MAutocomplete
                    key={preparationSelected?.id || "no-preparation"}
                    name="countingSelected"
                    options={
                      preparationSelected?.countings
                        .filter((count: Counting) => count.id)
                        .map((count: Counting, index: number) => ({
                          ...count,
                          index,
                        })) || []
                    }
                    label="Seleccione un contaje"
                    getOptionLabel={(option: { id: string; index: number }) =>
                      `Contaje ${option.index + 1}`
                    }
                    validation={{
                      required: "El contaje es requerido",
                    }}
                  />
                )}
            </ResponsiveFormContainer>
            <Box sx={{ ...styles.footerContainer }}>
              <MButton onClick={handleModalSave} text="Guardar" />
              <MButton
                onClick={handleCloseModal}
                text="Cerrar"
                variant="outlined"
              />
            </Box>
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default NavHistoryTritio;
